import { Button } from '@/components/ui/button'
import { TournamentGameState } from '@/components/play/tournament-game-state'
import type { RetryAffordance } from '@/lib/game-retry-card'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { useI18n } from '@/lib/i18n'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export type { RetryAffordance } from '@/lib/game-retry-card'

interface RetryCardProps {
  retry: RetryAffordance
  isErrored: boolean
  isRetrying: boolean
  onRetry: () => void | Promise<void>
  tournament?: boolean
}

function useSecondsUntil(timestampMs: number | null): number {
  const [remaining, setRemaining] = useState(() =>
    timestampMs ? Math.max(0, Math.ceil((timestampMs - Date.now()) / 1000)) : 0)

  useEffect(() => {
    if (!timestampMs)
      return
    const tick = () =>
      setRemaining(Math.max(0, Math.ceil((timestampMs - Date.now()) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [timestampMs])

  return timestampMs ? remaining : 0
}

export function RetryCard({
  retry,
  isErrored,
  isRetrying,
  onRetry,
  tournament = false,
}: RetryCardProps) {
  const { t } = useI18n()
  const cooldownSeconds = useSecondsUntil(retry.cooldownUntilMs)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const handleRetry = async () => {
    const restoreFocus = document.activeElement === buttonRef.current
    await onRetry()
    if (restoreFocus) {
      requestAnimationFrame(() => {
        if (document.activeElement === document.body
          || document.activeElement === buttonRef.current) {
          buttonRef.current?.focus()
        }
      })
    }
  }

  const explanation = retry.canRetry
    ? (isErrored ? t('play.retry.description') : t('play.retry.stuckDescription'))
    : retry.reason === 'retry_cooling_down'
      ? t('play.retry.reason.retryCoolingDown', { seconds: cooldownSeconds })
      : t(`play.retry.reason.${toCamel(retry.reason)}`)

  if (tournament) {
    return (
      <TournamentGameState state="retry">
        {!retry.canRetry && (
          <p role="status" className="text-base leading-6 text-primary">
            {explanation}
          </p>
        )}
        <Button
          ref={buttonRef}
          type="button"
          data-testid="tournament-retry"
          onClick={() => {
            void handleRetry()
          }}
          disabled={!retry.canRetry || isRetrying}
          aria-busy={isRetrying}
          className={`${gameStartButtonClassName} h-auto min-h-12 whitespace-normal text-base focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
        >
          <RotateCcw aria-hidden="true" className="size-4" />
          {isRetrying ? t('play.retry.retrying') : t('play.retry.button')}
        </Button>
      </TournamentGameState>
    )
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          aria-hidden="true"
          className="mt-0.5 size-5 flex-none text-amber-500"
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">
            {isErrored ? t('play.retry.title') : t('play.retry.stuckTitle')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{explanation}</p>

          {retry.canRetry && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button
                type="button"
                size="sm"
                onClick={onRetry}
                disabled={isRetrying}
              >
                <RotateCcw aria-hidden="true" className="mr-2 size-4" />
                {isRetrying ? t('play.retry.retrying') : t('play.retry.button')}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t('play.retry.remaining', { count: retry.retriesRemaining })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function toCamel(reason: string | null): string {
  if (!reason)
    return 'notRetryableState'
  return reason.replaceAll(/_(\w)/g, (_, c: string) => c.toUpperCase())
}
